import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';
import useDynamicFields from 'components/emailer/hooks/use-dynamic-fields';
import useFileUploader from 'components/emailer/hooks/use-file-uploader';
import { useEmailTemplatesProvider } from '../email-templates-context';
import { useEmailSignaturesProvider } from 'apps/outreach/email/signatures/email-signatures-context';
import {
  ModalHeaderWithActions,
  ModalHeader,
  Modal,
  ModalBody,
  ModalFooterWithButtons,
  Button,
  Icon,
} from '@evertrue/et-components';
import { Formik } from 'formik';
import SubjectInput from 'components/emailer/form/subject-input';
import MissingInfoModal from 'components/emailer/components/missing-information-modal';
import { setDefaultValues, emailTemplateSchema } from './email-template-schema';
import { RichTextEditor } from '@evertrue/sdk';
import { useIdentity } from 'base/identity-resolver';
import { useFormatEmailBody } from 'components/emailer/hooks/use-format-email-body';
import SignaturePickerModal from 'apps/cadences-v2/components/email-template/signature-picker-modal';
import DOMPurify from 'dompurify';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';
import cadenceGqlCall from 'apps/cadences-v2/utils/cadence-gql-call';
import { ORG_TAGS_QUERY, REGISTER_TAG_MUTATION } from 'apps/cadences-v2/utils/cadence-queries';

/**
 * Attempts to use context; returns safe defaults when rendered outside providers.
 * This allows the modal to work standalone (with template prop) or within providers.
 */
const useSafeEmailTemplatesProvider = () => {
  try {
    return useEmailTemplatesProvider();
  } catch {
    return {
      createEmailTemplate: null,
      updateEmailTemplate: null,
      selectedTemplate: {},
      setSelectedTemplate: () => {},
    };
  }
};

const useSafeEmailSignaturesProvider = () => {
  try {
    return useEmailSignaturesProvider();
  } catch {
    return { signatures: [] };
  }
};

/**
 * Email template form modal — supports create, edit, and read-only view modes.
 *
 * @param {Object} props
 * @param {Function} props.closeTemplateForm - Callback to close the modal
 * @param {Object} [props.template] - Optional template object to use directly (bypasses context)
 * @param {boolean} [props.readOnly] - When true, renders a view-only preview
 * @param {Function} [props.onSave] - Optional save callback (bypasses context update). Receives the saved values.
 */
const EmailTemplateFormModal = ({ closeTemplateForm, template: templateProp, readOnly = false, onSave }) => {
  const joditRef = useRef(null);
  const {
    org: { id: oid },
    user: { id: userId },
  } = useIdentity();
  const { createEmailTemplate, updateEmailTemplate, selectedTemplate, setSelectedTemplate } =
    useSafeEmailTemplatesProvider();
  const { signatures } = useSafeEmailSignaturesProvider();
  const { onImageUpload } = useFileUploader([], null);
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [bypassUnsupportedFields, setBypassUnsupportedFields] = useState(false);
  const [unsupportedFields, setUnsupportedFields] = useState([]);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [appliedSignatureId, setAppliedSignatureId] = useState(null);
  const { getUnsupportedFields, bodyHasMissingFields, dynamicFieldPlaceholders } = useDynamicFields({});
  const { removeParagraphTags } = useFormatEmailBody();

  const resolvedTemplate = templateProp || selectedTemplate;
  const { flagEnabled: signalEnabled } = useFeatureFlag('signal');
  const { flagEnabled: cadenceV2Enabled } = useFeatureFlag('cadenceV2Internal');
  const hasCadenceV2 = signalEnabled && cadenceV2Enabled;
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);

  useEffect(() => {
    if (!tagInput.trim()) {
      setTagSuggestions([]);
      return;
    }
    let stale = false;
    const timer = setTimeout(() => {
      cadenceGqlCall(ORG_TAGS_QUERY, { search: tagInput.trim() })
        .then((data) => {
          if (!stale) setTagSuggestions(data?.orgTags || []);
        })
        .catch(() => {
          if (!stale) setTagSuggestions([]);
        });
    }, 300);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [tagInput]);

  const handleSubmit = async (values) => {
    if (bodyHasMissingFields(values.templateBody)) {
      const fieldsNotSupported = getUnsupportedFields(values.templateBody);
      if (fieldsNotSupported.length > 0 && !bypassUnsupportedFields) {
        setUnsupportedFields(fieldsNotSupported);
        setShowReviewPopup(true);
        return;
      }
      values.isDynamicTemplate = true;
    }
    if (onSave) {
      await onSave(values);
    } else if (values.id) {
      const updatedTemplate = await updateEmailTemplate(values.id, oid, userId, values);
      setSelectedTemplate(updatedTemplate);
    } else {
      delete values.id;
      const newTemplate = await createEmailTemplate(values);
      setSelectedTemplate(newTemplate);
    }
    setBypassUnsupportedFields(false);
    setUnsupportedFields([]);
    closeTemplateForm();
  };

  if (readOnly) {
    return (
      <Modal isOpen={true} size="large">
        <ModalHeader title="View Email Template" closeModal={closeTemplateForm} />
        <ModalBody>
          <div className="email-form email-form--readonly">
            <div className="email-form-header">
              <div className="email-form--readonly-field">
                <strong className="email-form--readonly-label">Name</strong>
                <span>{resolvedTemplate?.templateName || '\u2014'}</span>
              </div>
              <hr className="email-form-header-hr" />
              <div className="email-form--readonly-field">
                <strong className="email-form--readonly-label">Subject</strong>
                <span>{resolvedTemplate?.templateSubject || '\u2014'}</span>
              </div>
              <hr className="email-form-header-hr" />
            </div>
            <div className="email-form--readonly-body">
              {resolvedTemplate?.templateBody ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resolvedTemplate.templateBody) }} />
              ) : (
                <p className="email-form--readonly-empty">No body content</p>
              )}
            </div>
            {(resolvedTemplate?.tags || []).length > 0 && (
              <div className="email-form-tags-row">
                <span className="email-form-tags-label">Tags</span>
                <div className="email-form-tags-list">
                  {resolvedTemplate.tags.map((tag) => (
                    <span key={tag} className="email-form-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooterWithButtons>
          <Button type="secondary" onClick={closeTemplateForm}>
            Close
          </Button>
        </ModalFooterWithButtons>
      </Modal>
    );
  }

  return (
    <Formik
      initialValues={setDefaultValues(resolvedTemplate, oid, userId, removeParagraphTags)}
      onSubmit={handleSubmit}
      enableReinitialize={false}
      validationSchema={emailTemplateSchema}
    >
      {({ handleSubmit, handleChange, setFieldValue, values, errors, touched, isValid, isSubmitting }) => {
        const handleAddTag = () => {
          const val = tagInput.trim().toLowerCase();
          const existingLower = (values.tags || []).map((t) => t.toLowerCase());
          if (val && !existingLower.includes(val)) {
            setFieldValue('tags', [...(values.tags || []), val]);
            cadenceGqlCall(REGISTER_TAG_MUTATION, { name: val }).catch((e) => {
              console.warn('[EmailTemplate] Failed to register tag in org registry:', e?.message);
            });
          }
          setTagInput('');
          setTagSuggestions([]);
        };
        return (
          <>
            <Modal isOpen={true} size="large">
              <ModalHeaderWithActions
                title={!values.id ? 'Create Email Template' : 'Edit Email Template'}
                closeModal={closeTemplateForm}
                onSubmit={handleSubmit}
                disableSubmit={!isValid}
                size="small"
              />
              <div className="email-form et--modal-body">
                <div className="email-form-header">
                  <SubjectInput
                    name="Name"
                    field="templateName"
                    value={values.templateName}
                    setFieldValue={setFieldValue}
                  />
                  <hr className="email-form-header-hr" />
                  <SubjectInput
                    name="Subject"
                    value={values.templateSubject}
                    field="templateSubject"
                    setFieldValue={setFieldValue}
                  />
                  <hr className="email-form-header-hr" />
                </div>
                <div className="rte-container" style={{ height: '300px', maxHeight: '300px', overflow: 'hidden' }}>
                  <RichTextEditor
                    ref={joditRef}
                    value={values.templateBody}
                    onChange={handleChange('templateBody')}
                    dynamicFields={dynamicFieldPlaceholders}
                    additionalConfig={{
                      resize: false,
                      height: '320px',
                    }}
                    onImageUpload={onImageUpload}
                  />
                </div>
                {hasCadenceV2 && (
                  <div className="email-form-signature-row">
                    <Button
                      type="secondary"
                      onClick={() => setShowSignatureModal(true)}
                      className="email-form-signature-btn"
                    >
                      <Icon icon="text-format" />
                      {appliedSignatureId != null && appliedSignatureId !== 0
                        ? `Signature: ${(signatures || []).find((s) => s.id === appliedSignatureId)?.signature_name || 'Selected'}`
                        : 'Add Signature'}
                    </Button>
                    {appliedSignatureId != null && appliedSignatureId !== 0 && (
                      <Button
                        type="simple"
                        onClick={() => {
                          setAppliedSignatureId(null);
                          const currentBody = values.templateBody || '';
                          const sigSep = '<!-- et-signature -->';
                          const sepIdx = currentBody.indexOf(sigSep);
                          if (sepIdx !== -1) {
                            setFieldValue('templateBody', currentBody.slice(0, sepIdx));
                          }
                        }}
                      >
                        <Icon icon="close" /> Remove Signature
                      </Button>
                    )}
                  </div>
                )}
                {hasCadenceV2 && (
                  <div className="email-form-tags-row">
                    <label className="email-form-tags-label">Tags</label>
                    <div className="email-form-tags-input-wrapper">
                      <input
                        type="text"
                        className="email-form-tags-input"
                        placeholder="Add a tag..."
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTag();
                          }
                        }}
                      />
                      <Button type="action" onClick={handleAddTag} disabled={!tagInput.trim()}>
                        Add
                      </Button>
                      {tagSuggestions.length > 0 && tagInput.length > 0 && (
                        <div className="create-cadence-configure--tag-dropdown">
                          {tagSuggestions
                            .filter((s) => !(values.tags || []).map((t) => t.toLowerCase()).includes(s.toLowerCase()))
                            .map((suggestion) => (
                              <button
                                key={suggestion}
                                type="button"
                                className="create-cadence-configure--tag-dropdown-item"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setFieldValue('tags', [...(values.tags || []), suggestion.toLowerCase()]);
                                  setTagInput('');
                                  setTagSuggestions([]);
                                }}
                              >
                                {suggestion}
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                    {(values.tags || []).length > 0 && (
                      <div className="email-form-tags-list">
                        {values.tags.map((tag) => (
                          <span key={tag} className="email-form-tag">
                            {tag}
                            <button
                              type="button"
                              onClick={() =>
                                setFieldValue(
                                  'tags',
                                  values.tags.filter((t) => t !== tag)
                                )
                              }
                            >
                              <Icon icon="close" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Modal>
            {hasCadenceV2 && (
              <SignaturePickerModal
                isOpen={showSignatureModal}
                onClose={() => setShowSignatureModal(false)}
                signatures={signatures || []}
                onSelect={(sig) => {
                  if (sig.id === 0) {
                    // "No Signature" chosen — strip any existing appended signature
                    setAppliedSignatureId(null);
                    const currentBody = values.templateBody || '';
                    const sigSep = '<!-- et-signature -->';
                    const sepIdx = currentBody.indexOf(sigSep);
                    if (sepIdx !== -1) {
                      setFieldValue('templateBody', currentBody.slice(0, sepIdx));
                    }
                  } else {
                    setAppliedSignatureId(sig.id);
                    // Strip any previously appended signature then append the new one
                    const currentBody = values.templateBody || '';
                    const sigSep = '<!-- et-signature -->';
                    const baseBody = currentBody.includes(sigSep)
                      ? currentBody.slice(0, currentBody.indexOf(sigSep))
                      : currentBody;
                    setFieldValue(
                      'templateBody',
                      `${baseBody}${sigSep}<div style="margin-top:16px;border-top:1px solid #e0e0e0;padding-top:12px">${sig.signature_body}</div>`
                    );
                  }
                  setShowSignatureModal(false);
                }}
                currentSignatureId={appliedSignatureId}
              />
            )}
            <MissingInfoModal
              title="Review Unsupported Dynamic Fields"
              secondaryButtonLabel="Save Anyway"
              missingFields={unsupportedFields}
              handleSubmit={() => {
                setBypassUnsupportedFields(true);
                handleSubmit(values);
              }}
              isOpen={showReviewPopup}
              close={() => setShowReviewPopup(false)}
            />
          </>
        );
      }}
    </Formik>
  );
};

EmailTemplateFormModal.propTypes = {
  closeTemplateForm: PropTypes.func,
  template: PropTypes.object,
  readOnly: PropTypes.bool,
  onSave: PropTypes.func,
};

export default EmailTemplateFormModal;
